import type {
  SignatureCertificateRefInfo,
  SignatureConnectionInfo,
  SignatureSigningProfileInfo,
  SignatureWorkspacePolicyInfo,
} from './use-workspace-pdf-signatures';

type ConnectionState = Pick<SignatureConnectionInfo, 'id' | 'health'>;
// `pinState` is absent in answers of a server from before the fixed PIN.
type CertificateState = Pick<
  SignatureCertificateRefInfo,
  'id' | 'connectionId' | 'status' | 'expiresAt'
> &
  Partial<Pick<SignatureCertificateRefInfo, 'pinState'>>;
type ProfileState = Pick<
  SignatureSigningProfileInfo,
  'id' | 'status' | 'connectionId' | 'certificateRefId'
>;

/**
 * A signature connection signing can use: its last check did not fail and
 * its access is neither revoked nor expired. Never checked counts as usable.
 */
export function isSignatureConnectionUsable(
  connection: Pick<ConnectionState, 'health'>
): boolean {
  return connection.health === 'healthy' || connection.health === 'unknown';
}

/** Expired by its state or by its date, whichever says so first. */
export function isSignatureCertificateExpired(
  certificate: Pick<CertificateState, 'status' | 'expiresAt'>,
  now: number = Date.now()
): boolean {
  if (certificate.status === 'expired') return true;
  if (!certificate.expiresAt) return false;
  return new Date(certificate.expiresAt).getTime() <= now;
}

/** The provider wants a fixed PIN that is missing or that it refused. */
export function isSignatureCertificatePinPending(
  certificate: Pick<CertificateState, 'pinState'>
): boolean {
  return (
    certificate.pinState === 'missing' || certificate.pinState === 'rejected'
  );
}

/** A certificate people can bind to a signing profile and sign with. */
export function isSignatureCertificateUsable(
  certificate: Pick<CertificateState, 'status' | 'expiresAt' | 'pinState'>,
  now: number = Date.now()
): boolean {
  return (
    certificate.status === 'active' &&
    !isSignatureCertificateExpired(certificate, now) &&
    !isSignatureCertificatePinPending(certificate)
  );
}

export interface SigningReadinessInput<Profile extends ProfileState> {
  connections: ReadonlyArray<ConnectionState>;
  certificates: ReadonlyArray<CertificateState>;
  profiles: ReadonlyArray<Profile>;
  policy: Pick<SignatureWorkspacePolicyInfo, 'allowedConnectionIds'> | null;
  now?: number;
}

type OwnedProfileState = ProfileState &
  Pick<
    SignatureSigningProfileInfo,
    'readinessStatus' | 'testSignStatus' | 'failureReason' | 'isDefault'
  >;

/**
 * How one signing profile stands for its owner: ready to sign, or the one
 * thing that keeps it from signing.
 */
export type SigningProfileState =
  | 'ready'
  | 'needs_activation'
  | 'activation_failed'
  | 'disabled'
  | 'connection_unavailable'
  | 'connection_not_allowed'
  | 'certificate_unusable'
  | 'certificate_pin_missing';

export function resolveSigningProfileState(
  profile: OwnedProfileState,
  {
    connections,
    certificates,
    policy,
    now = Date.now(),
  }: Omit<SigningReadinessInput<OwnedProfileState>, 'profiles'>
): SigningProfileState {
  if (profile.status !== 'active') return 'disabled';
  const allowed = policy?.allowedConnectionIds ?? [];
  if (allowed.length > 0 && !allowed.includes(profile.connectionId)) {
    return 'connection_not_allowed';
  }
  const connection = connections.find(item => item.id === profile.connectionId);
  if (
    !connection ||
    !isSignatureConnectionUsable(connection) ||
    profile.readinessStatus === 'provider_outage'
  ) {
    return 'connection_unavailable';
  }
  const certificate = certificates.find(
    item => item.id === profile.certificateRefId
  );
  if (
    certificate?.connectionId === connection.id &&
    certificate.status === 'active' &&
    !isSignatureCertificateExpired(certificate, now) &&
    isSignatureCertificatePinPending(certificate)
  ) {
    return 'certificate_pin_missing';
  }
  if (
    !certificate ||
    certificate.connectionId !== connection.id ||
    !isSignatureCertificateUsable(certificate, now) ||
    profile.readinessStatus === 'expired' ||
    profile.readinessStatus === 'revoked'
  ) {
    return 'certificate_unusable';
  }
  if (profile.readinessStatus === 'test_sign_passed') return 'ready';
  return profile.testSignStatus === 'failed' || profile.failureReason
    ? 'activation_failed'
    : 'needs_activation';
}

/** What a workspace still lacks before a member can set up a signing profile. */
export type ProfileSetupGap = 'none' | 'connection' | 'certificate';

/** The connections new signing profiles may use: usable and allowed. */
export function selectProfileConnections<Connection extends ConnectionState>(
  connections: ReadonlyArray<Connection>,
  policy: Pick<SignatureWorkspacePolicyInfo, 'allowedConnectionIds'> | null
): Connection[] {
  const allowed = policy?.allowedConnectionIds ?? [];
  return connections.filter(
    connection =>
      isSignatureConnectionUsable(connection) &&
      (allowed.length === 0 || allowed.includes(connection.id))
  );
}

/**
 * A usable connection whose certificates are all revoked or expired is not
 * the same as no connection: the remedy lies with the certificates.
 */
export function resolveProfileSetupGap({
  connections,
  certificates,
  policy,
  now = Date.now(),
}: Omit<SigningReadinessInput<ProfileState>, 'profiles'>): ProfileSetupGap {
  const usable = new Set(
    selectProfileConnections(connections, policy).map(
      connection => connection.id
    )
  );
  if (usable.size === 0) return 'connection';
  return certificates.some(
    certificate =>
      usable.has(certificate.connectionId) &&
      isSignatureCertificateUsable(certificate, now)
  )
    ? 'none'
    : 'certificate';
}

/** The owner can settle these states in place by activating the profile. */
export function canActivateSigningProfile(state: SigningProfileState): boolean {
  return (
    state === 'needs_activation' ||
    state === 'activation_failed' ||
    state === 'disabled'
  );
}

/** Where the missing piece of a profile is settled. */
export type SigningProfileRemedy =
  | 'activate'
  | 'personal_settings'
  | 'workspace_settings';

export function resolveSigningProfileRemedy(
  state: Exclude<SigningProfileState, 'ready'>
): SigningProfileRemedy {
  if (canActivateSigningProfile(state)) return 'activate';
  return state === 'certificate_unusable'
    ? 'personal_settings'
    : 'workspace_settings';
}

/**
 * Where the reader stands on the way to a digitally signed copy. Every step
 * but the last names the one thing that is still missing.
 */
export type DigitalSigningStep<Profile extends OwnedProfileState> =
  | { kind: 'hidden' }
  | { kind: 'upgrade_plan'; requiredPlan: string | null }
  | { kind: 'connect_service' }
  | { kind: 'create_profile' }
  | {
      kind: 'fix_profile';
      profile: Profile;
      state: Exclude<SigningProfileState, 'ready'>;
    }
  | { kind: 'ready'; profiles: Profile[] };

const FIX_ORDER: ReadonlyArray<Exclude<SigningProfileState, 'ready'>> = [
  'needs_activation',
  'activation_failed',
  'certificate_unusable',
  'certificate_pin_missing',
  'connection_unavailable',
  'connection_not_allowed',
  'disabled',
];

/**
 * Members without a usable signature connection and without a profile see nothing:
 * there is nothing they could do. Everyone else gets the next step.
 *
 * While the workspace plan lacks the digital signature, nobody signs: admins
 * and people who had set up a profile learn that the plan is what is missing.
 */
export function resolveDigitalSigningStep<Profile extends OwnedProfileState>(
  input: SigningReadinessInput<Profile> & {
    isAdmin: boolean;
    /** Set while the workspace plan does not include the digital signature. */
    planLock?: { requiredPlan: string | null } | null;
  }
): DigitalSigningStep<Profile> {
  if (input.planLock) {
    return input.isAdmin || input.profiles.length > 0
      ? { kind: 'upgrade_plan', requiredPlan: input.planLock.requiredPlan }
      : { kind: 'hidden' };
  }
  const states = input.profiles.map(profile => ({
    profile,
    state: resolveSigningProfileState(profile, input),
  }));
  const ready = states
    .filter(item => item.state === 'ready')
    .map(item => item.profile)
    .sort((a, b) => Number(b.isDefault) - Number(a.isDefault));
  if (ready.length > 0) return { kind: 'ready', profiles: ready };

  const connected = input.connections.some(isSignatureConnectionUsable);
  if (!connected && input.isAdmin) return { kind: 'connect_service' };

  for (const wanted of FIX_ORDER) {
    const match = states.find(item => item.state === wanted);
    if (match) {
      return { kind: 'fix_profile', profile: match.profile, state: wanted };
    }
  }
  return connected ? { kind: 'create_profile' } : { kind: 'hidden' };
}
